import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../config/fbConfig';
import SuccessModal from './SuccessModal';

const AddStudentsToClassModal = ({ classId, isOpen, onClose, onSuccess }) => {
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [classData, setClassData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', middleName: '' });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all students and detect duplicates
        const studentsSnap = await getDocs(collection(db, 'students'));
        const studentsData = [];
        const studentIds = new Set();
        const duplicates = {};

        studentsSnap.docs.forEach(doc => {
          const student = { id: doc.id, ...doc.data(), docPath: doc.ref.path };
          if (studentIds.has(student.id)) {
            // Store duplicate for logging
            if (!duplicates[student.id]) {
              duplicates[student.id] = [];
              // Add the original document that was already processed
              const original = studentsData.find(s => s.id === student.id);
              if (original) {
                duplicates[student.id].push({
                  ...original,
                  timestamp: original.updatedAt || original.createdAt || new Date().toISOString()
                });
              }
            }
            duplicates[student.id].push({
              ...student,
              timestamp: student.updatedAt || student.createdAt || new Date().toISOString()
            });
            console.warn(`Duplicate student ID found: ${student.id}`, {
              docPath: student.docPath,
              data: student
            });
          } else {
            studentIds.add(student.id);
            studentsData.push(student);
          }
        });

        // Log a summary of duplicates
        if (Object.keys(duplicates).length > 0) {
          console.warn('Duplicate student IDs summary:', {
            duplicateIds: Object.keys(duplicates),
            details: duplicates
          });
          // Handle duplicates by selecting the most recent or complete record
          Object.entries(duplicates).forEach(([id, records]) => {
            // Sort by timestamp or completeness
            const selectedRecord = records.sort((a, b) => {
              const aTime = new Date(a.timestamp || 0).getTime();
              const bTime = new Date(b.timestamp || 0).getTime();
              if (aTime !== bTime) return bTime - aTime; // Prefer newer
              return Object.keys(b).length - Object.keys(a).length; // More complete
            })[0];
            // Remove the original if it exists
            const existingIndex = studentsData.findIndex(s => s.id === id);
            if (existingIndex !== -1) {
              studentsData.splice(existingIndex, 1);
            }
            studentsData.push(selectedRecord);
          });
        } else {
          console.log('No duplicate student IDs found.');
        }

        // Fetch class data
        const classSnap = await getDoc(doc(db, 'classes', classId));
        let classData = null;
        if (classSnap.exists()) {
          classData = { id: classSnap.id, ...classSnap.data() };
          classData.students = [...new Set(classData.students || [])];
        } else {
          console.warn(`Class with ID ${classId} not found.`);
        }

        setStudents(studentsData);
        setClassData(classData);
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId]);

  const handleSelect = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(sid => sid !== id);
      }
      return [...new Set([...prev, id])];
    });
  };

  const handleAdd = async () => {
    if (!classData) return;

    const { subjects = [] } = classData;
    const classRef = doc(db, 'classes', classId);

    const psychomotorSkills = {
      handwriting: 'A',
      fluency: 'A',
      games: 'A',
      punctuality: 'A',
      neatness: 'A',
      politeness: 'A',
      cooperation: 'A',
      leadership: 'A',
      attentiveness: 'A',
      honesty: 'A'
    };

    setLoading(true);

    try {
      for (const studentId of selectedIds) {
        const studentRef = doc(db, 'students', studentId);

        const subjectsWithScores = subjects.map(subject => ({
          subjectId: subject.id || '',
          teacherId: subject.teacherId || '',
          scores: {
            firstca: 0,
            secondca: 0,
            exam: 0,
          }
        }));

        const studentSnap = await getDoc(studentRef);
        if (studentSnap.exists()) {
          const studentData = studentSnap.data();
          const isAlreadyEnrolled = studentData.enrollments?.some(e => e.classId === classId);

          if (!isAlreadyEnrolled) {
            const updatedEnrollments = [...(studentData.enrollments || []), {
              classId,
              subjects: subjectsWithScores,
              psychomotorSkills
            }];
            await updateDoc(studentRef, { enrollments: updatedEnrollments });
          }
        } else {
          console.warn(`Student with ID ${studentId} does not exist.`);
        }

        await updateDoc(classRef, {
          students: arrayUnion(studentId)
        });
      }

      const classSnap = await getDoc(classRef);
      if (classSnap.exists()) {
        setClassData({ id: classSnap.id, ...classSnap.data(), students: [...new Set(classSnap.data().students || [])] });
      }

      onSuccess();
      setSelectedIds([]);
      onClose();
    } catch (error) {
      console.error("Error adding students to class:", error);
      alert("Failed to add students. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (studentId) => {
    setError('');
    setSuccessMessage('');
    try {      
      const classRef = doc(db, 'classes', classId);
      const studentRef = doc(db, 'students', studentId);

      await updateDoc(classRef, {
        students: arrayRemove(studentId)
      });

      const studentSnap = await getDoc(studentRef);
      if (studentSnap.exists()) {
        const studentData = studentSnap.data();
        const updatedEnrollments = (studentData.enrollments || []).filter(e => e.classId !== classId);

        await updateDoc(studentRef, {
          enrollments: updatedEnrollments
        });
      } else {
        setError(`Student with ID ${studentId} does not exist.`);
        setShowModal(true);
      }

      const classSnap = await getDoc(classRef);
      if (classSnap.exists()) {
        const updatedClassData = { id: classSnap.id, ...classSnap.data(), students: [...new Set(classSnap.data().students || [])] };
        if (!updatedClassData.students.includes(studentId)) {
          setClassData(updatedClassData);
        } else {
          setError(`Failed to remove student ${studentId} from class ${classId} students array.`);
          setShowModal(true);
        }
      } else {
        setError(`Class with ID ${classId} does not exist.`);
        setShowModal(true);
      }

      onSuccess();
    } catch (error) {
      setError("Failed to remove student. Please try again.");
      setShowModal(true);
    }
  };

  const handleEdit = async (studentId) => {
    setError('');
    setSuccessMessage('');
    if (editingStudentId === studentId) {
      // Save changes
      try {
        const studentRef = doc(db, 'students', studentId);
        await updateDoc(studentRef, {
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          middleName: editForm.middleName || ''
        });

        // Update local students state
        setStudents(prev => prev.map(s => 
          s.id === studentId 
            ? { ...s, firstName: editForm.firstName, lastName: editForm.lastName, middleName: editForm.middleName || '' }
            : s
        ));

        setEditingStudentId(null);
        setSuccessMessage('Student name updated successfully!');
        setShowModal(true);
      } catch (error) {
        setError("Failed to update student name.");
        setShowModal(true);
      }
    } else {
      // Start editing
      const student = students.find(s => s.id === studentId);
      if (student) {
        setEditForm({
          firstName: student.firstName || '',
          lastName: student.lastName || '',
          middleName: student.middleName || ''
        });
        setEditingStudentId(studentId);
      }
    }
  };

  if (loading || !classData) {
    return (
      <Modal isOpen={isOpen} onRequestClose={onClose}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <p>Processing...</p>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}
      className="custom-modal"
      overlayClassName="custom-modal-overlay"
    >
      <p className='size5 caps'>Select Students</p>

      <div>
        <strong>Already Enrolled:</strong>
        <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
          {classData.students?.length > 0 ? (
            classData.students.map((studentId, index) => {
              const student = students.find(s => s.id === studentId);
              if (!student) return null;

              const isEditing = editingStudentId === student.id;

              return (
                <div key={`${student.id}-${index}`} className="row space-between align-center" style={{ padding: '8px 0', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <input
                          value={editForm.firstName}
                          onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                          placeholder="First Name"
                          className='input-field1'
                          style={{padding: '5px', marginBottom: '0px'}}
                        />
                        <input
                          value={editForm.middleName}
                          onChange={(e) => setEditForm({ ...editForm, middleName: e.target.value })}
                          placeholder="Middle Name"
                          className='input-field1'
                          style={{padding: '5px', marginBottom: '0px'}}
                        />
                        <input
                          value={editForm.lastName}
                          onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                          placeholder="Last Name"
                          className='input-field1'
                          style={{padding: '5px', marginBottom: '0px'}}
                        />
                      </div>
                    ) : (
                      <span>
                        {student.firstName} {student.lastName} {student.middleName && `${student.middleName} `}{' '}
                        <span className='size5'>({student.admissionNumber})</span>
                      </span>
                    )}
                  </div>

                  <div className="row gap-1">
                    <button
                      onClick={() => handleEdit(student.id)}
                      className="list-btn"
                      title={isEditing ? "Save" : "Edit Name"}
                    >
                      <i className={`fas ${isEditing ? 'fa-check' : 'fa-edit'}`}></i>
                    </button>
                    {isEditing && (
                      <button
                        onClick={() => setEditingStudentId(null)}
                        className="list-btn"
                        style={{ background: '#fc796f' }}
                        title="Cancel"
                      >
                        <i className="fas fa-times font-white"></i>
                      </button>
                    )}
                    {!isEditing && (
                      <button onClick={() => handleRemove(student.id)} className="list-btn">
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p>No students enrolled yet.</p>
          )}
        </div>
      </div>

      <hr />
      
      <div>
        <strong>Available Students:</strong>
        <input
          type="text"
          placeholder="Search by name or admission number"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="input-field1"
          style={{ marginBottom: '10px' }}
        />
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {students
            .filter(stu => {
              // Exclude students already enrolled in the class
              if (classData?.students?.includes(stu.id)) {
                return false;
              }
              const query = searchText.toLowerCase();
              const nameParts = [
                stu.firstName?.toLowerCase() || '',
                stu.middleName?.toLowerCase() || '',
                stu.lastName?.toLowerCase() || ''
              ].filter(Boolean); // Remove empty parts
              
              // Check if all query terms appear in any name part
              const queryTerms = query.split(' ');
              return queryTerms.every(term =>
                nameParts.some(part => part.includes(term))
                || stu.admissionNumber?.toLowerCase().includes(query)
              );
            })
            .map((stu, index) => (
              <div key={`${stu.id}-${index}`} style={{ padding: '5px 0' }}>
                <label>
                  <input
                    className='input-checkbox gold-tick male-female-stat'
                    type="checkbox"
                    checked={selectedIds.includes(stu.id)}
                    onChange={() => handleSelect(stu.id)}
                  />
                  {stu.firstName} {stu.lastName} {stu.middleName}{' '}
                  <br /><span className='size5'>({stu.admissionNumber})</span>
                </label>
              </div>
            ))}
        </div>
      </div>

      <div className="row align-center gap-1" style={{ marginTop: '10px' }}>
        <button className="login-button" type="button" onClick={onClose}>Cancel</button>
        <button className="login-button" type="button" onClick={handleAdd} disabled={loading || selectedIds.length === 0}>
          {loading ? 'Adding...' : 'Add'}
        </button>
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
    </Modal>
  );
};

export default AddStudentsToClassModal;